import {
  forwardRef,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import {
  type A11yStatusMessageOptions,
  useCombobox,
  type UseComboboxProps,
} from 'downshift';
import styles from './combobox.module.scss';

export interface ComboboxItem<TValue extends string = string> {
  value: TValue;
  label?: string;
  disabled?: boolean;

  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface ComboboxProps<TItem extends ComboboxItem = ComboboxItem> {
  getAsyncItems: (search: string) => Promise<TItem[]>;
  value?: TItem['value'];
  onChange?: (value: TItem['value']) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: () => void;
  tooltipIcon?: ReactNode;
  label: string;
  id?: string;
  name?: string;
  defaultValue?: TItem['value'];
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  noResultsMessage?: string;
  getA11yStatusMessage?: (opts: A11yStatusMessageOptions<TItem>) => string;
  getA11ySelectionMessage?: (opts: A11yStatusMessageOptions<TItem>) => string;
  'data-testid'?: string;
}

function itemToString<TItem extends ComboboxItem = ComboboxItem>(
  item: TItem | null,
): string {
  function htmlDecode(input: string | undefined) {
    if (!input) return '';
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.documentElement.textContent;
  }

  return htmlDecode(item?.label) ?? htmlDecode(item?.value) ?? '';
}

function findItem(
  items: ComboboxItem[],
  value?: string | null,
): (typeof items)[number] | null {
  return value ? items.find((item) => item.value === value) ?? null : null;
}

const ComboBox = forwardRef<HTMLInputElement, ComboboxProps>(
  (
    {
      getAsyncItems,
      onChange,
      onBlur,
      onFocus,
      value,
      defaultValue = null,
      tooltipIcon = <></>,
      label = '',
      id,
      name = '',
      placeholder = '',
      disabled = false,
      readOnly = false,
      invalid = false,
      noResultsMessage = 'No results.',
      getA11yStatusMessage,
      getA11ySelectionMessage,
      'data-testid': testId = 'combobox',
    },
    ref,
  ) => {
    type Item = Awaited<ReturnType<typeof getAsyncItems>>[number];
    const _id = useId();
    const [items, setItems] = useState<Item[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [inputCursor, setInputCursor] = useState<number | null>(null);
    const [defaultItem, setDefaultItem] = useState<Item | null>(null);
    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    const [loading, setLoading] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const inputRef = useRef<HTMLInputElement>(null);

    // track cursor position
    useEffect(() => {
      const input = inputRef.current;
      if (input) input.setSelectionRange(inputCursor, inputCursor);
    }, [inputRef, inputCursor, inputValue]);

    // get items
    useEffect(() => {
      if (!inputValue) {
        setItems([]);
        return;
      }
      setLoading(true);

      try {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(async () => {
          const newItems = await getAsyncItems(inputValue);
          setItems(newItems ?? []);
        }, 300);
      } catch (error) {
        // TODO: show error message when we can't get items
      }
      setLoading(false);

      return () => {
        clearTimeout(timeoutRef.current);
      };
    }, [inputValue, getAsyncItems]);

    // get default value
    useEffect(() => {
      if (!defaultValue) return;
      const asyncEffect = async () => {
        const newItems = await getAsyncItems(defaultValue);
        const newDefault = findItem(newItems, defaultValue);
        if (!newDefault) return;
        setItems(newItems);
        setDefaultItem(newDefault);
        setSelectedItem(newDefault);
      };
      void asyncEffect();
    }, [defaultValue]);

    const handleChange = (newValue: typeof value = '') => {
      if (!newValue) setInputValue('');
      onChange?.(newValue);
    };

    const comboboxProps: UseComboboxProps<(typeof items)[number]> = {
      id: id || _id,
      items,
      inputValue,
      defaultSelectedItem: defaultItem,
      selectedItem,
      onSelectedItemChange: (changes) => {
        setSelectedItem(changes.selectedItem ?? null);
        handleChange(changes.selectedItem?.value);
      },
      onInputValueChange: ({ inputValue: newValue }) => {
        setInputValue(newValue ?? '');
      },
      isItemDisabled(item) {
        return Boolean(item.disabled);
      },
      stateReducer(__, { type, changes }) {
        switch (type) {
          // auto-highlight first result
          case useCombobox.stateChangeTypes.InputClick:
          case useCombobox.stateChangeTypes.InputChange:
            if (changes?.highlightedIndex === -1) {
              return {
                ...changes,
                highlightedIndex: items.findIndex((__item) => !__item.disabled),
                selectedItem: null,
              };
            }

            if (changes.inputValue?.length) {
              return {
                ...changes,
                highlightedIndex: items.findIndex((__item) => !__item.disabled),
              };
            }

            return changes;
          // otherwise business as usual.
          default:
            return changes;
        }
      },
      itemToString,
    };
    // null value is valid, explicitly undefined is not
    if (value !== undefined) {
      comboboxProps.selectedItem = findItem(items, value);
    }
    if (getA11yStatusMessage) {
      comboboxProps.getA11yStatusMessage = getA11yStatusMessage;
    }
    if (getA11ySelectionMessage) {
      comboboxProps.getA11ySelectionMessage = getA11ySelectionMessage;
    }

    const {
      getLabelProps,
      getInputProps,
      getMenuProps,
      getItemProps,
      highlightedIndex,
      isOpen,
    } = useCombobox(comboboxProps);

    const handleClear = () => {
      setSelectedItem(null);
      handleChange('');
      setItems([]);
      inputRef?.current?.focus();
    };

    return (
      <div className={styles.combobox + (invalid ? ` ${styles.invalid}` : '')}>
        <div>
          <div className={styles.tooltipWrapper}>
            <label {...getLabelProps()} className={styles.label}>
              {label}
            </label>
            &nbsp;
            {tooltipIcon}
          </div>
          <div className={styles.input}>
            <input
              {...getInputProps({
                type: 'text',
                placeholder,
                disabled,
                readOnly,
                name,
                onBlur,
                onFocus,
                onChange(e: React.ChangeEvent<HTMLInputElement>) {
                  setInputCursor(e.target.selectionStart);
                },
                ref(e) {
                  // need to account for callback refs
                  if (typeof ref === 'function') {
                    ref?.(e);
                  } else if (ref) {
                    ref.current = e;
                  }
                  (
                    inputRef as React.MutableRefObject<HTMLInputElement | null>
                  ).current = e;
                },
              })}
              data-testid={`${testId}-input`}
            />
            {selectedItem && (
              // TODO: Replace with "unset" Button component variant
              <button
                aria-label="clear value"
                title="clear"
                //TODO: define this style
                // className={styles.inputIcon}
                onClick={handleClear}
                disabled={readOnly || disabled}
                data-testid={`${testId}-clear-btn`}
              ></button>
            )}
          </div>
        </div>
        <ul {...getMenuProps()} data-testid={`${testId}-menu`}>
          {isOpen && (
            <>
              {/* no items */}
              {!loading && !items.length && Boolean(inputValue.length) && (
                <li aria-hidden data-no-results={true}>
                  {noResultsMessage}
                </li>
              )}
              {/* resolved items */}
              {Boolean(inputValue.length) &&
                items.map((item, index) => (
                  <li
                    className={
                      highlightedIndex === index ? styles.highlighted : ''
                    }
                    key={item.value}
                    {...getItemProps({
                      index,
                      item,
                    })}
                  >
                    <span>{itemToString(item)}</span>
                  </li>
                ))}
            </>
          )}
        </ul>
      </div>
    );
  },
);

export default ComboBox;
